import { useFavoriteAssignmentsQuery } from './graphql';
import { useFavoriteAssignmentsMutations } from '@mp/hooks/mutations';
import { useState, useEffect, useRef } from 'react';

interface UseFavoriteButtonLogicParams {
  assignmentId: number;
  onlyUnapplied?: boolean;
}

export const useFavoriteButtonLogic = ({
  assignmentId,
  onlyUnapplied,
}: UseFavoriteButtonLogicParams) => {
  const { ids } = useFavoriteAssignmentsQuery({ onlyUnapplied });
  const { markAsFavoriteMutation, unmarkAsFavoriteMutation } = useFavoriteAssignmentsMutations();

  const isCurrentlyFavorited = ids.includes(assignmentId);

  const [optimisticState, setOptimisticState] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const lastServerState = useRef<boolean>(isCurrentlyFavorited);
  const processingTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (lastServerState.current !== isCurrentlyFavorited) {
      lastServerState.current = isCurrentlyFavorited;
      setOptimisticState(null);
      setIsProcessing(false);

      if (processingTimeout.current) {
        clearTimeout(processingTimeout.current);
        processingTimeout.current = null;
      }
    }
  }, [isCurrentlyFavorited]);

  const toggleFavorite = () => {
    if (isProcessing) {
      return;
    }

    const newState = !isCurrentlyFavorited;
    setOptimisticState(newState);
    setIsProcessing(true);

    processingTimeout.current = setTimeout(() => {
      setIsProcessing(false);
    }, 2000);

    const onSettled = () => {
      setIsProcessing(false);
      if (processingTimeout.current) {
        clearTimeout(processingTimeout.current);
        processingTimeout.current = null;
      }
    };

    const onError = () => {
      setOptimisticState(null);
      onSettled();
    };

    if (newState) {
      markAsFavoriteMutation({ assignmentId }, { onSettled, onError });
    } else {
      unmarkAsFavoriteMutation({ assignmentId }, { onSettled, onError });
    }
  };

  const isFavorited = optimisticState !== null ? optimisticState : isCurrentlyFavorited;

  return {
    isFavorited,
    toggleFavorite,
  };
};
